<script setup lang="ts">
/* =============================================================
   BERTH RESULT CARD
   =============================================================
   One berth in a search: what it is, how far, whether the ship
   fits and the berth is free, what the stay costs, and how it
   confirms -- everything §4 says a user should see "without
   searching through long descriptions or contacting the owner".

   Selecting the card is the map's job too (it highlights the
   pin); opening it goes to the berth profile; the compare box is
   separate so ticking it never navigates.
   ============================================================= */

import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import Badge from '@/components/ui/Badge.vue'
import Icon from '@/components/Icon.vue'
import { useAmenities } from '@/lib/amenities'
import { formatEur } from '@/lib/bookingLabels'
import { bookingModeText, formatDistance, resultStatus } from '@/lib/searchLabels'
import type { BerthResult } from '@/lib/types'
import type { RouteLocationRaw } from 'vue-router'

const props = defineProps<{
  result: BerthResult
  hasStay: boolean
  selected?: boolean
  compared?: boolean
  /** Hide the compare box -- e.g. when already at the comparison limit and not ticked. */
  compareDisabled?: boolean
  to: RouteLocationRaw
}>()

const emit = defineEmits<{ select: []; toggleCompare: [] }>()

const { amenityLabel } = useAmenities()
const status = computed(() => resultStatus(props.result, props.hasStay))
const dim = computed(() => !props.result.fits || (props.hasStay && !status.value.bookable))
</script>

<template>
  <article
    class="card"
    :class="{ selected, dim }"
    :data-berth="result.id"
    @click="emit('select')"
  >
    <header class="top">
      <div class="titles">
        <RouterLink :to="to" class="name" @click.stop>{{ result.name }}</RouterLink>
        <p class="where">
          {{ result.city }} · {{ formatDistance(result.distance_km) }}
          <template v-if="result.company_name"> · {{ result.company_name }}</template>
        </p>
      </div>
      <div class="price">
        <template v-if="hasStay && result.price_eur !== null">
          <span class="amount">{{ formatEur(result.price_eur) }}</span>
          <span class="per">for this stay</span>
        </template>
      </div>
    </header>

    <div class="facts">
      <Badge :tone="status.tone">{{ status.label }}</Badge>
      <span class="mode">
        <Icon :name="result.booking_mode === 'instant' ? 'check' : 'clock'" :size="14" />
        {{ bookingModeText(result.booking_mode, result.response_hours) }}
      </span>
      <span v-if="result.max_length_m !== null" class="limit">up to {{ result.max_length_m }} m</span>
    </div>

    <p v-if="status.detail" class="detail" :class="status.tone">{{ status.detail }}</p>

    <ul v-if="result.amenities.length" class="amenities">
      <li v-for="a in result.amenities" :key="a">{{ amenityLabel(a) }}</li>
    </ul>

    <footer class="actions">
      <label class="compare" :class="{ disabled: compareDisabled && !compared }" @click.stop>
        <input
          type="checkbox"
          :checked="compared"
          :disabled="compareDisabled && !compared"
          @change="emit('toggleCompare')"
        />
        Compare
      </label>
      <RouterLink :to="to" class="open" @click.stop>
        Details <Icon name="chevronRight" :size="14" />
      </RouterLink>
    </footer>
  </article>
</template>

<style scoped>
.card {
  display: flex;
  flex-direction: column;
  gap: var(--space-2xs);
  padding: var(--space-sm) var(--space-md);
  background-color: var(--color-surface);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition:
    border-color var(--dur-fast) var(--ease),
    box-shadow var(--dur-fast) var(--ease);
}

.card:hover {
  border-color: var(--color-border-strong);
}

.selected,
.selected:hover {
  border-color: var(--color-accent);
  box-shadow: var(--shadow-focus);
}

.dim {
  background-color: var(--color-surface-sunken);
}

.dim .name,
.dim .amount {
  color: var(--color-text-muted);
}

.top {
  display: flex;
  justify-content: space-between;
  gap: var(--space-sm);
}

.titles {
  min-width: 0;
}

.name {
  font-size: var(--text-base);
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
  text-decoration: none;
}

.name:hover {
  color: var(--color-accent);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.where {
  margin-top: 2px;
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.price {
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: end;
}

.amount {
  font-size: var(--text-md);
  font-weight: var(--weight-bold);
  font-variant-numeric: tabular-nums;
  color: var(--color-heading);
}

.per {
  font-size: var(--text-xs);
  color: var(--color-text-faint);
}

.facts {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2xs) var(--space-xs);
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.mode {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3xs);
}

.detail {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.detail.danger {
  color: var(--color-danger);
}

.detail.warning {
  color: var(--color-accent-2);
}

.amenities {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3xs);
  margin: 0;
  padding: 0;
  list-style: none;
}

.amenities li {
  padding: 0.1em var(--space-2xs);
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  background-color: var(--color-surface-sunken);
  border-radius: var(--radius-full);
}

.dim .amenities li {
  background-color: var(--color-surface);
}

.actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: var(--space-3xs);
  font-size: var(--text-sm);
}

.compare {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2xs);
  color: var(--color-text-muted);
  cursor: pointer;
}

.compare input {
  width: 1rem;
  height: 1rem;
  accent-color: var(--color-accent);
}

.compare.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.open {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-weight: var(--weight-semibold);
  color: var(--color-accent);
  text-decoration: none;
}

.open:hover {
  text-decoration: underline;
  text-underline-offset: 2px;
}
</style>
